import { useState, useEffect, useRef } from 'react';

import type { OperationResult } from 'urql';
import { CombinedError, useClient } from 'urql';

import { config } from '../../../config/config';
import type {
    IGetTransactionQuery,
    IGetTransactionQueryVariables,
} from '../queries/cashier.graphql';
import { GetTransactionDocument } from '../queries/cashier.graphql';

/**
 * Gets the transaction information for the specified user and transaction id.
 * Retries on error as some providers notify the frontend of the transaction being completed
 * before its even on our system.
 */
export function useTransaction({
    userProfileId,
    transactionId,
}: {
    userProfileId: string;
    transactionId?: string;
}) {
    const lastFetchInformation = useRef({ start: Date.now(), attempts: 0 });
    const [responseState, setResponseState] = useState<
        Omit<
            OperationResult<IGetTransactionQuery, IGetTransactionQueryVariables>,
            'operation' | 'stale' | 'hasNext'
        > & {
            fetching: boolean;
        }
    >({ data: undefined, fetching: true, error: undefined });

    const client = useClient();

    useEffect(() => {
        if (!transactionId || !userProfileId) {
            return;
        }

        let cancelled = false;
        let timeoutId: null | number = null;
        const request = async () => {
            const handleError = (error: CombinedError) => {
                const isRetrying = lastFetchInformation.current.attempts < 5;

                setResponseState({
                    fetching: isRetrying,
                    error,
                    data: undefined,
                });

                if (isRetrying) {
                    timeoutId = setTimeout(request, 2000);
                }
            };

            lastFetchInformation.current.attempts++;
            try {
                const { data, error } = await client
                    .query<IGetTransactionQuery, IGetTransactionQueryVariables>(
                        GetTransactionDocument,
                        {
                            userProfileId,
                            transactionId,
                            xTenantID: config.TENANT_ID,
                        },
                        {
                            requestPolicy: 'network-only',
                        },
                    )
                    .toPromise();

                if (cancelled) {
                    return;
                }

                if (error) {
                    handleError(error);
                    return;
                }

                setResponseState({
                    data,
                    error,
                    fetching: false,
                });
            } catch (e) {
                handleError(new CombinedError({ networkError: e as Error }));
            }
        };

        request();
        return () => {
            cancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [transactionId, userProfileId, client]);

    return responseState;
}
